const d3 = require("d3");
const { getCumulativeWaitingTimesPerGroup } = require("@/visualisation/graphs/waitingTimeAnimations/waitingTimeMath.js");
const { TransitionCircleVisualiser } = require("@/visualisation/graphs/waitingTimeAnimations/transitionCircles.js");

class LineVisualiser {
    constructor(graphShape, resourceGroups, simName) {
        this.graphShape = graphShape;
        this.resourceGroups = resourceGroups;
        this.simName = simName;

    }

    createAllLines(resourceLineGroup, resourceTransitionLinesGroup, transitionCirclesGroup, defs, resourceNodes, projectNodes) {
        this.resourceLineGroup = resourceLineGroup;
        this.transitionCirclesGroup = transitionCirclesGroup;
        this.resourceTransitionLinesGroup = resourceTransitionLinesGroup;
        this.defs = defs;

        this.transitionPaths = {};
        this.visualPathGroups = {};

        this.createResourceLines(resourceNodes);
        this.createProjectLines(resourceNodes, projectNodes)

        this.transitionCirclesVisualiser = new TransitionCircleVisualiser(this.transitionPaths, this.transitionCirclesGroup, resourceNodes, projectNodes)
    }

    createProjectLines(resourceNodes, projectNodes) {

        var projectInput = projectNodes[0];
        var projectOutput = projectNodes[1];

        var projectInputConnection = resourceNodes[projectInput.connections];
        var projectOutputConnection = resourceNodes[projectOutput.connections];

        this.visualPathGroups["X" + projectInputConnection.id] = this.resourceLineGroup.append("g");
        this.visualPathGroups[projectOutputConnection.id + "X"]  = this.resourceLineGroup.append("g");

        const inputPath = d3.path();
        inputPath.moveTo(projectInput.x, projectInput.y);
        inputPath.lineTo(projectInputConnection.x, projectInputConnection.y);

        const outputPath = d3.path();
        outputPath.moveTo(projectOutputConnection.x, projectOutputConnection.y);
        outputPath.lineTo(projectOutput.x, projectOutput.y);
        
        this.transitionPaths["X" + projectInputConnection.id] = this.resourceTransitionLinesGroup.append("path").style("fill", "none").style("stroke-width", "8px").style("stroke", "#0000004d").style("opacity", 1).attr("d", inputPath);
        this.transitionPaths[projectOutputConnection.id + "X"] = this.resourceTransitionLinesGroup.append("path").style("fill", "none").style("stroke-width", "8px").style("stroke", "#0000004d").style("opacity", 1).attr("d", outputPath);

    }

    createResourceLines(resourceNodes) {
        var resourceGroupIDs = Object.keys(resourceNodes);

        // Create direct resource lines between every resource group combination
        for (let id1 of resourceGroupIDs) {

            this.createSelfTransitionLine(resourceNodes[id1])

            var connections = resourceNodes[id1].connections;
            if (this.graphShape == "circle") {
                connections = Object.keys(resourceNodes);
            }

            for (let id2 of connections) {

                if (id1 == id2) {
                    continue;
                }

                this.createSingleResourceLine(resourceNodes[id1], resourceNodes[id2]);
            }
        }
    }

    createSelfTransitionLine(resourceNode) {

        var xRotation = 5;
        var largeArc = 1;
        var sweep = 0;
        var drx = 40;
        var dry = 40;

        const path = "M" + (resourceNode.x + 1) + "," + (resourceNode.y + 1) + "A" + drx + "," + dry + " " + xRotation + "," + largeArc + "," + sweep + " " + (resourceNode.x) + "," + (resourceNode.y);
        
        this.visualPathGroups[resourceNode.id + resourceNode.id] = this.resourceLineGroup.append("g")
        this.visualPathGroups[resourceNode.id + resourceNode.id].append("path").style("fill", "none").style("mix-blend-mode", "darken").style("stroke-width", "5px").style("stroke", `gray`).style("opacity", 0).attr("d", path);
        this.transitionPaths[resourceNode.id + resourceNode.id]  = this.resourceTransitionLinesGroup.append("path").style("fill", "none").style("stroke-width", "2px").style("stroke", "#0000004d").attr("d", path).attr("opacity", 0);
    }


    createPaths(graphShape, fromNode, toNode) {
        var pathMapper = { "circle": this.createQuadraditicPaths, "lineair": this.createLinearPaths };
        return pathMapper[graphShape].bind(this)(fromNode, toNode);
    }

    createLinearPaths(fromNode, toNode) {
        const fromP = { x: fromNode.x, y: fromNode.y };
        const toP = { x: toNode.x, y: toNode.y };

        const path = d3.path();
        path.moveTo(fromP.x, fromP.y);
        path.lineTo(toP.x, toP.y);

        this.visualPathGroups[fromNode.id + toNode.id] = this.resourceLineGroup.append("g");
        this.transitionPaths[fromNode.id + toNode.id] = this.resourceTransitionLinesGroup.append("path").style("fill", "none").style("stroke-width", "2px").style("stroke", "#0000004d").style("opacity", 0).attr("d", path);

        var key = fromNode.id + toNode.id;

        return { [key]: path };
    }


    createQuadraditicPaths(fromNode, toNode) {
        const fromP = { x: fromNode.x, y: fromNode.y };
        const toP = { x: toNode.x, y: toNode.y };
        const controlP = { x: 0, y: 0 }; // The control point in the middle

        const midPoint = {
            x: (fromP.x + 2 * controlP.x + toP.x) / 4,
            y: (fromP.y + 2 * controlP.y + toP.y) / 4,
        };

        // Create the first split curve (from fromP to midPoint)
        const path1 = d3.path();
        path1.moveTo(fromP.x, fromP.y);
        path1.quadraticCurveTo((fromP.x + controlP.x) / 2, (fromP.y + controlP.y) / 2, midPoint.x, midPoint.y);

        // Create the second split curve (from midPoint to toP)
        const path2 = d3.path();
        path2.moveTo(midPoint.x, midPoint.y);
        path2.quadraticCurveTo((controlP.x + toP.x) / 2, (controlP.y + toP.y) / 2, toP.x, toP.y);

        this.visualPathGroups[fromNode.id + toNode.id] = this.resourceLineGroup.append("g");
        this.visualPathGroups[toNode.id + fromNode.id] = this.resourceLineGroup.append("g");

        const path = d3.path();
        //Create the quadratic curve path from the two points and the control point in the middle
        path.moveTo(fromP.x, fromP.y);
        path.quadraticCurveTo(controlP.x, controlP.y, toP.x, toP.y);
        this.transitionPaths[fromNode.id + toNode.id] = this.resourceTransitionLinesGroup.append("path").style("fill", "none").style("stroke-width", "2px").style("stroke", "#0000004d").style("opacity", 0).attr("d", path);

        //Apply gradient
        const reversePath = d3.path();
        reversePath.moveTo(toP.x, toP.y);
        reversePath.quadraticCurveTo(0, 0, fromP.x, fromP.y);

        this.transitionPaths[toNode.id + fromNode.id] = this.resourceTransitionLinesGroup.append("path").style("fill", "none").style("stroke-width", "2px").style("stroke", "#0000004d").style("opacity", 0).attr("d", reversePath);


        var key1 = fromNode.id + toNode.id;
        var key2 = toNode.id + fromNode.id;

        return { [key1]: path1, [key2]: path2 };
    }

    updateLines(newMetrics, withoutCircles) {
        var waitingTimeResults = getCumulativeWaitingTimesPerGroup(newMetrics, this.resourceGroups);
        var cumulativeWaitingTimes = waitingTimeResults.cumulativeWaitingTimes;
        var exponent = 2;
        var max = Math.max(waitingTimeResults.max, waitingTimeResults.maxThreshold / 4);

        var widthScale = d3.scalePow().exponent(exponent).domain([waitingTimeResults.min, max]).range([5, 12]);
        var opacityScale = d3.scalePow().exponent(exponent).domain([waitingTimeResults.min, max]).range([0.35, 1]);

        // Loop over the cumulative waiting times and update the lines
        for (let i = 0; i < cumulativeWaitingTimes.length; i++) {
            var currCumulativeWaitingTime = cumulativeWaitingTimes[i];
            var group1 = currCumulativeWaitingTime.group1;
            var group2 = currCumulativeWaitingTime.group2;

            var val = currCumulativeWaitingTime.val;
            var val2 = newMetrics.cumulativeWaitingTimesPerGroup[group2.id][group1.id];

            var maxVal = Math.max(val, val2);

            

            //Replace with dynamic system later
            if(this.simName == "sim26"){
                maxVal = Math.min(15, maxVal);
            }

            var newWidth = widthScale(maxVal);
            var newOpacity = opacityScale(maxVal);

            if (maxVal == 0) {
                newOpacity = 0;
                newWidth = 0;
            }

            if (this.graphShape != "lineair") {
                this.setGradientMap(val, val2, group1, group2, false);
            }

            this.updateSingleLine(group1, group2, newOpacity, newWidth);

        }
        

        if(!withoutCircles){
            this.transitionCirclesVisualiser.updateCircles(newMetrics, this.visualPathGroups);
        }
    }
    updateSingleLine(group1, group2, newOpacity, newWidth) {
        var lineVisualGroup = this.visualPathGroups[group1.id + group2.id];

        if (lineVisualGroup == undefined) {
            return;
        }

        lineVisualGroup.select("path").transition().duration(250).ease(d3.easeLinear).style("opacity", newOpacity).style("stroke-width", newWidth + "px");
    }

    createSingleResourceLine(fromNode, toNode) {
        var paths = this.createPaths(this.graphShape, fromNode, toNode);

        const gradientID1 = `gradient${fromNode.id}${toNode.id}`;
        const gradientID2 = `gradient${toNode.id}${fromNode.id}`;

        this.setGradientMap(0, 0, fromNode, toNode, true);
        this.setGradientMap(0, 0, toNode, fromNode, true);

        this.visualPathGroups[fromNode.id + toNode.id].append("path").style("fill", "none").style("mix-blend-mode", "darken").style("stroke-width", "5px").style("stroke", `url(#${gradientID1})`).style("opacity", 0).attr("d", paths[fromNode.id + toNode.id]);

        if (Object.keys(paths).length > 1) {
            this.visualPathGroups[toNode.id + fromNode.id].append("path").style("fill", "none").style("mix-blend-mode", "darken").style("stroke-width", "5px").style("stroke", `url(#${gradientID2})`).style("opacity", 0).attr("d", paths[toNode.id + fromNode.id]);
        }
    }

    setGradientMap(group1Val, group2Val, fromNode, toNode, initialise) {
        const startX = fromNode.x;
        const startY = fromNode.y;
        const endX = toNode.x;
        const endY = toNode.y; /* y-coordinate of the ending point */

        const controlP = { x: 0, y: 0 }; // The control point in the middle
        //Get the point halfway the path
        // Calculate the midpoint of the curve
        const midPoint = {
            x: (fromNode.x + 2 * controlP.x + toNode.x) / 4,
            y: (fromNode.y + 2 * controlP.y + toNode.y) / 4,
        };

        const gradientRadius = Math.sqrt((midPoint.x - startX) ** 2 + (midPoint.y - startY) ** 2);
        const gradientID = `gradient${fromNode.id}${toNode.id}`;

        var gradient = this.defs.select(`#${gradientID}`);
        if (initialise) {
            gradient = this.defs
                .append("radialGradient")
                .attr("id", gradientID)
                .attr("gradientUnits", "userSpaceOnUse")
                .attr("cx", startX) // x-coordinate of the starting point
                .attr("cy", startY) // y-coordinate of the starting point
                .attr("r", gradientRadius); // Radius is set to the calculated distanc
        }

        // Calculate the radius of the gradient as the distance between start and end points

        var differenceVal = group1Val - group2Val;
        var maxDifference = 10;
        var minDifference = -10;

        differenceVal = Math.max(minDifference, differenceVal);
        differenceVal = Math.min(maxDifference, differenceVal);

        //Scale the differenceVal to a value between -1 and 1
        differenceVal = (2 * differenceVal) / (maxDifference - minDifference);

        //Now scale it to a value between 0 and 1
        differenceVal = (differenceVal + 1) / 2;

        
        differenceVal = differenceVal * differenceVal;
        
        differenceVal = 1 - differenceVal;
        var interpolationFunction = d3.interpolateRdBu;
        interpolationFunction = d3.interpolateYlOrRd;
        interpolationFunction = d3.interpolateRdBu;
        interpolationFunction = d3.interpolateRgb("red", "blue");
        interpolationFunction = d3.interpolateOrRd;
        interpolationFunction = d3.interpolateGreys;
        interpolationFunction = d3.interpolateRgb("white", "black");
        interpolationFunction = d3.interpolateGreys;


        var col1 = interpolationFunction(differenceVal);
        var col2 = interpolationFunction(0.5);

        gradient.selectAll("stop").remove();

        gradient.append("stop").attr("offset", "0%").attr("stop-color", col1);
        gradient.append("stop").attr("offset", "100%").attr("stop-color", col2);
    }
}

module.exports = { LineVisualiser };
